import type {AxiosError} from "axios";

import {residentClaimApi} from "../api";
import type {
  CompleteClaimRequest,
  ResidentClaimApiResponse,
  ResidentClaimCompleteData,
  ResidentClaimProfileData,
  ResidentClaimProfileRequest,
  ResidentClaimStartData,
  ResidentClaimVerifyData,
  StartClaimRequest,
} from "../types";

class ResidentClaimService {
  private assertSuccess<T>(response: ResidentClaimApiResponse<T>): T {
    if (response.success !== 1 || response.data === undefined) {
      const error = new Error(response.message || "Request failed") as Error & {
        response?: {data: ResidentClaimApiResponse<T>};
      };
      error.response = {data: response};
      throw error;
    }
    return response.data;
  }

  private async handle<T>(promise: Promise<{data: ResidentClaimApiResponse<T>}>): Promise<T> {
    try {
      const {data} = await promise;
      return this.assertSuccess(data);
    } catch (error) {
      if ((error as AxiosError).response?.data) throw error;
      throw error;
    }
  }

  startClaim(body: StartClaimRequest): Promise<ResidentClaimStartData> {
    return this.handle(residentClaimApi.startClaim(body));
  }

  completeClaim(
    body: CompleteClaimRequest,
  ): Promise<{data: ResidentClaimCompleteData; message: string}> {
    return this.handleWithMessage(residentClaimApi.completeClaim(body));
  }

  verifyClaim(token: string): Promise<{data: ResidentClaimVerifyData; message: string}> {
    return this.handleWithMessage(residentClaimApi.verifyClaim(token));
  }

  completeProfile(
    body: ResidentClaimProfileRequest,
  ): Promise<{data: ResidentClaimProfileData; message: string}> {
    return this.handleWithMessage(residentClaimApi.completeProfile(body));
  }

  private async handleWithMessage<T>(
    promise: Promise<{data: ResidentClaimApiResponse<T>}>,
  ): Promise<{data: T; message: string}> {
    const {data} = await promise;
    if (data.success !== 1 || data.data === undefined) {
      const error = new Error(data.message || "Request failed") as Error & {
        response?: {data: ResidentClaimApiResponse<T>};
      };
      error.response = {data};
      throw error;
    }
    return {data: data.data, message: data.message};
  }
}

export const residentClaimService = new ResidentClaimService();
